/**
 * Hendelsesskjema (AP11, 08 §E / 14-maling-og-samtykke.md).
 *
 *   window.skjld.track(name, params)
 *
 * Hver hendelse går ALLTID til førstepartsendepunktet POST /api/t/e (ingen
 * cookie, ingen persondata – vår egen nevner), og til GA4 KUN når besøkende
 * har gitt analysesamtykke og gtag.js er lastet av consent.js.
 *
 * Markup-kontrakt (gjelder både classic og «next»-forsiden):
 *   [data-section="hero"]            → section_view (IntersectionObserver, én gang)
 *   [data-cta="book-primary"]        → cta_click {cta_id, section, target}
 *   a[href] til annet vertsnavn      → outbound_click {domain, product, section}
 *   [data-track-view="book_view"]    → hendelsen sendes ved sidelast
 *       + data-track-params='{"type":"booking"}'  (JSON, valgfritt)
 *   [data-track-form="book"]         → book_form_start {field} ved første fokus
 *   scroll 25/50/75/90               → scroll {percent}
 *
 * Tillatte navn og parameternøkler kommer fra window.skjld.config.events
 * (config/tracking.php); ukjente navn droppes lokalt og avvises på serveren.
 */

const SCROLL_MARKS = [25, 50, 75, 90];

function config() {
    return (window.skjld && window.skjld.config) || {};
}

function sectionOf(node) {
    const section = node && node.closest ? node.closest('[data-section]') : null;
    return section ? section.getAttribute('data-section') : null;
}

function sendFirstParty(payload) {
    const endpoint = config().endpoint;
    if (!endpoint) return;
    const body = JSON.stringify(payload);

    try {
        if (navigator.sendBeacon) {
            const ok = navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
            if (ok) return;
        }
    } catch (e) { /* fall tilbake på fetch */ }

    try {
        fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body,
            keepalive: true,
            credentials: 'same-origin',
        }).catch(() => {});
    } catch (e) { /* ignorer */ }
}

function sendGa4(name, params) {
    const consent = window.skjld && window.skjld.consent && window.skjld.consent.get();
    if (!consent || !consent.analytics || !config().ga4 || typeof window.gtag !== 'function') return;
    window.gtag('event', name, params);
}

/**
 * Meta-hendelser (Sjur 14.10, ts 1791958863).
 *
 * `Schedule` paa fullfoert booking fyres av <x-meta-pixel> i nettleseren og av
 * MetaConversionsApi paa serveren, med samme `event_id`, og roeres IKKE her: en booking
 * skal telles én gang, og kampanjen optimaliserer paa den ene hendelsen. Sjur avviste
 * derfor `Lead` paa `book_submit` — det er samme booking ett steg tidligere.
 *
 * Det eneste denne veien sender er `Lead`, og bare fra KONTAKTSKJEMAET. Booking-sidene
 * sender ogsaa `generate_lead`, med `type: 'booking'`, og de skal ikke gi `Lead`.
 *
 * Ingen persondata foelger med — bare hendelsen. Pixelen finnes bare i nettleseren etter
 * markedsfoeringssamtykke (consent.js laster fbevents.js foerst da), saa `window.fbq`
 * er udefinert uten samtykke og kallet skjer ikke.
 */
function sendMeta(name, params) {
    if (name !== 'generate_lead' || !params || params.type !== 'contact') return;
    const consent = window.skjld && window.skjld.consent && window.skjld.consent.get();
    if (!consent || !consent.marketing || typeof window.fbq !== 'function') return;
    window.fbq('track', 'Lead');
}

function track(name, params) {
    const allowed = config().events || [];
    if (!name || allowed.indexOf(name) === -1) return;

    const clean = {};
    Object.keys(params || {}).forEach((key) => {
        const value = params[key];
        if (value === null || value === undefined) return;
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            clean[key] = typeof value === 'string' ? value.slice(0, 200) : value;
        }
    });

    const base = {
        locale: config().locale || document.documentElement.lang || null,
        variant: config().variant || null,
        path: window.location.pathname,
    };

    sendFirstParty({ name, params: clean, path: base.path, locale: base.locale, variant: base.variant });
    sendGa4(name, Object.assign({}, clean, { locale: base.locale, variant: base.variant, page_section: clean.section || null }));
    sendMeta(name, clean);
}

// ---------------------------------------------------------------------------
// Automatiske hendelser
// ---------------------------------------------------------------------------

function initSectionViews() {
    const sections = document.querySelectorAll('[data-section]');
    if (!sections.length || !('IntersectionObserver' in window)) return;

    const seen = new Set();
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const name = entry.target.getAttribute('data-section');
            if (!name || seen.has(name) || name === 'nav' || name === 'footer') return;
            seen.add(name);
            track('section_view', { section: name });
            observer.unobserve(entry.target);
        });
    }, { threshold: 0.4 });

    sections.forEach((section) => observer.observe(section));
}

function initClicks() {
    document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href], button[data-cta]');
        if (!link) return;

        const cta = link.getAttribute('data-cta');
        const href = link.getAttribute('href') || '';
        const section = sectionOf(link);

        if (cta) {
            track('cta_click', { cta_id: cta, section, target: href.slice(0, 200) });
        }

        if (link.tagName === 'A' && href && !href.startsWith('#')) {
            let url;
            try { url = new URL(link.href, window.location.href); } catch (e) { return; }
            if (url.host && url.host !== window.location.host && /^https?:$/.test(url.protocol)) {
                const domain = url.hostname.replace(/^www\./, '');
                const products = config().products || {};
                track('outbound_click', { domain, product: link.getAttribute('data-product') || products[domain] || null, section });
            }
        }
    }, { capture: true, passive: true });
}

function initScrollDepth() {
    const fired = new Set();
    let ticking = false;

    const check = () => {
        ticking = false;
        const doc = document.documentElement;
        const total = doc.scrollHeight - window.innerHeight;
        if (total <= 0) return;
        const percent = Math.round(((window.scrollY || doc.scrollTop) / total) * 100);
        SCROLL_MARKS.forEach((mark) => {
            if (percent >= mark && !fired.has(mark)) {
                fired.add(mark);
                track('scroll', { percent: mark });
            }
        });
    };

    window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(check);
    }, { passive: true });
}

function initViewEvents() {
    document.querySelectorAll('[data-track-view]').forEach((node) => {
        const name = node.getAttribute('data-track-view');
        let params = {};
        const raw = node.getAttribute('data-track-params');
        if (raw) {
            try { params = JSON.parse(raw) || {}; } catch (e) { params = {}; }
        }
        track(name, params);
    });
}

function initFormStart() {
    document.querySelectorAll('[data-track-form]').forEach((form) => {
        const kind = form.getAttribute('data-track-form');
        let started = false;
        form.addEventListener('focusin', (event) => {
            if (started) return;
            const field = event.target;
            if (!field || !/^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test(field.tagName)) return;
            started = true;
            const label = field.getAttribute('name') || field.getAttribute('id') || field.getAttribute('type') || field.tagName.toLowerCase();
            track(kind === 'book' ? 'book_form_start' : 'form_start', { field: label.slice(0, 60) });
        });
    });
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

/**
 * Sidevisning, og om dette er den første siden i nettleserøkta.
 *
 * «Besøk» uten id: markøren ligger i sessionStorage og sendes aldri med – bare en
 * boolsk `entry`. Nettleseren tømmer den når fanen lukkes, så neste besøk teller på
 * nytt. Private vinduer og blokkert lagring faller tilbake til entry=true, altså en
 * liten overtelling av besøk framfor en stille feil.
 */
// Grov enhetsbøtte til MarketRadar (Sjur 22.10.2026, ts 1792690351). Vi leser skjermbredde
// og pekertype, ALDRI user agent: en UA-streng er nær unik og hører ikke hjemme i en tabell
// som med vilje ikke kan knyttes til en person. Tre bøtter er alt kroppen hans har felt for.
function enhet() {
    try {
        const bred = window.matchMedia('(min-width: 1024px)').matches;
        const fin = window.matchMedia('(pointer: fine)').matches;

        if (bred && fin) return 'pc';
        if (window.matchMedia('(min-width: 600px)').matches) return 'nettbrett';

        return 'mobil';
    } catch (e) {
        return 'pc';
    }
}

function sendPageView() {
    let entry = true;

    try {
        entry = window.sessionStorage.getItem('skjld_okt') === null;
        if (entry) window.sessionStorage.setItem('skjld_okt', '1');
    } catch (e) {
        entry = true;
    }

    track('page_view', { entry, device: enhet() });
}

export function initTracking() {
    window.skjld = window.skjld || {};
    window.skjld.track = track;

    sendPageView();
    initClicks();
    initScrollDepth();
    initSectionViews();
    initFormStart();
    initViewEvents();
}

export default initTracking;
